import * as React from 'react';
import {useJsonForms, withJsonFormsControlProps} from '@jsonforms/react';
import Text from "./Text";
import {SchemaUtils} from "../../util/SchemaUtils";
import {ControlValueFactory} from "../../factory/ControlValueFactory";
import {Component} from "../../enum/Component";
import { useMemo, useCallback } from 'react';

interface TextControlProps {
    data: any;
    handleChange(path: string, value: string): void;
    path: string;
    uischema: any;
    schema: any;
    errors: any;
    rootSchema: any;
    appValidated?: boolean;
}

const TextControl = ({ errors, uischema, schema, handleChange, path, data, rootSchema, appValidated }: TextControlProps) => {

    const jsonForms = useJsonForms();

    // Memoize expensive computations
    const enabled = useMemo(() =>
            SchemaUtils.isComponentEnabled(uischema, jsonForms.core!.data),
        [uischema, jsonForms.core!.data]
    );

    const visible = useMemo(() =>
            SchemaUtils.isComponentVisible(uischema, jsonForms.core!.data),
        [uischema, jsonForms.core!.data]
    );

    const required = useMemo(() =>
            SchemaUtils.isRequired(path, rootSchema),
        [path, rootSchema]
    );

    const value = useMemo(() =>
            ControlValueFactory.getControlValue(Component.Text, data, uischema.scope, jsonForms, enabled && visible),
        [Component.Text, data, uischema.scope, jsonForms, enabled, visible]
    );

    // Memoize the change handler
    const handleTextChange = useCallback((ev: any) => {
        handleChange(path, ev.target.value);
    }, [handleChange, path]);

    // Memoize the Text component props to prevent unnecessary re-renders
    const textProps = useMemo(() => ({
        variant: 'outlined' as const,
        error: errors,
        value: value,
        required: required,
        label: uischema.label,
        description: schema.description,
        onChange: handleTextChange,
        minLength: schema.minLength,
        maxLength: schema.maxLength,
        enabled: enabled,
        visible: visible,
        autoCapitalize: schema.autoCapitalize,
        appValidated: appValidated
    }), [
        errors,
        value,
        required,
        uischema.label,
        schema.description,
        handleTextChange,
        schema.minLength,
        schema.maxLength,
        enabled,
        visible,
        schema.autoCapitalize,
        appValidated
    ]);

    return <Text {...textProps} />;
};

// Export with memoization
export default React.memo(withJsonFormsControlProps(TextControl));

